import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, AlertTriangle, CalendarClock, Link2, Info, RefreshCw } from "lucide-react";

const fmtData = (d) => (d ? d.split("-").reverse().join("/") : "-");

const SEV = { alta: "bg-red-100 text-red-700", media: "bg-amber-100 text-amber-700", baixa: "bg-slate-100 text-slate-600" };
const TIPO_ICON = { duplicidade: AlertTriangle, ja_baixado_data_divergente: CalendarClock, sugestao_vinculo: Link2, observacao: Info };
const TIPO_TXT = {
  duplicidade: "Duplicidade", ja_baixado_data_divergente: "Já baixado / data divergente",
  sugestao_vinculo: "Sugestão de vínculo", observacao: "Observação",
};

export function AnaliseIADialog({ open, onClose, loading, analise, onAnalisar }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-analise-ia">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />Análise Inteligente do Extrato
          </DialogTitle>
          {analise && !loading && (
            <DialogDescription className="text-xs">
              Gerada em {new Date(analise.gerada_em).toLocaleString("pt-BR")} · {analise.linhas_analisadas} linhas × {analise.lancamentos_analisados} lançamentos · modelo {analise.modelo}
            </DialogDescription>
          )}
        </DialogHeader>
        {loading && (
          <div className="py-10 text-center space-y-3" data-testid="ia-loading">
            <Sparkles className="h-8 w-8 mx-auto text-purple-500 animate-pulse" />
            <p className="text-sm text-slate-500">A IA está cruzando o extrato com os lançamentos do sistema…<br />Isso pode levar até 1 minuto.</p>
          </div>
        )}
        {!loading && analise && (
          <div className="space-y-3">
            {analise.resumo && (
              <p className="text-sm p-3 rounded-lg bg-purple-50 border border-purple-100 text-slate-700" data-testid="ia-resumo">{analise.resumo}</p>
            )}
            {(analise.achados || []).length === 0 && (
              <p className="text-sm text-slate-400 p-4 text-center">Nenhum achado relevante — extrato sem anomalias detectadas.</p>
            )}
            {(analise.achados || []).map((a, i) => {
              const Icon = TIPO_ICON[a.tipo] || Info;
              return (
                <div key={i} className="p-3 rounded-lg border bg-white space-y-1" data-testid={`ia-achado-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                    <span className="text-sm font-semibold text-luvcorp-navy">{a.titulo}</span>
                    <Badge className={`text-[10px] ${SEV[a.severidade] || SEV.baixa}`}>{a.severidade}</Badge>
                    <Badge variant="outline" className="text-[10px]">{TIPO_TXT[a.tipo] || a.tipo}</Badge>
                    {a.confianca != null && <span className="text-[10px] text-slate-400 ml-auto">confiança {a.confianca}%</span>}
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{a.descricao}</p>
                </div>
              );
            })}
          </div>
        )}
        {!loading && !analise && <p className="text-sm text-slate-400 p-6 text-center">Nenhuma análise gerada ainda.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-fechar-analise-ia">Fechar</Button>
          <Button onClick={onAnalisar} disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-atualizar-analise-ia">
            <RefreshCw className="h-4 w-4 mr-1" />{analise ? "Atualizar análise" : "Analisar agora"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VincularBaixadoDialog({ data, onClose, onVincular }) {
  if (!data) return null;
  const { linha, alerta } = data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-vincular-baixado">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-orange-600" />Vincular a lançamento já baixado
          </DialogTitle>
          <DialogDescription className="text-xs">
            "{alerta.descricao}" já foi baixado em <b>{fmtData(alerta.data_pagamento)}</b>, e o extrato registra a movimentação em <b>{fmtData(linha.data)}</b>.
          </DialogDescription>
        </DialogHeader>
        {!alerta.mesma_data && (
          <div className="p-2.5 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-800">
            <b>Datas divergentes.</b> Escolha se a data da baixa deve ser corrigida para a data real do extrato — isso afeta o fluxo de caixa do período.
          </div>
        )}
        <div className="space-y-2">
          <Button className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
            onClick={() => onVincular(true)} data-testid="btn-vincular-corrigir-data">
            Vincular e corrigir data da baixa para {fmtData(linha.data)}
          </Button>
          <Button variant="outline" className="w-full" onClick={() => onVincular(false)} data-testid="btn-vincular-manter-data">
            Vincular mantendo a data da baixa ({fmtData(alerta.data_pagamento)})
          </Button>
          <Button variant="ghost" className="w-full" onClick={onClose} data-testid="btn-cancelar-vincular-baixado">Cancelar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
